import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';

import {
  TUI_VALIDATION_ERRORS,
  TuiDataListWrapperModule,
  TuiFieldErrorPipeModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiSelectModule,
  TuiInputPasswordModule,
} from '@taiga-ui/kit';
import {
  TuiAlertModule,
  TuiAlertService,
  TuiButtonModule,
  TuiDataListModule,
  TuiErrorModule,
  TuiLoaderModule,
  TuiTextfieldControllerModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserService } from '../../../core/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtils } from '../../../core/config/app.utils';
import { AuthService } from '../../../modules/auth/services/auth.service';

@Component({
  selector: 'app-add-user',
  standalone: true,
  imports: [
    TuiInputModule,
    CommonModule,
    TuiSelectModule,
    TuiDataListModule,
    TuiDataListWrapperModule,
    TuiInputPhoneModule,
    FormsModule,
    ReactiveFormsModule,
    TuiTextfieldControllerModule,
    TuiButtonModule,
    TuiFieldErrorPipeModule,
    TuiErrorModule,
    TuiAlertModule,
    TuiLoaderModule,
    TuiSvgModule,
    TuiInputPasswordModule,
  ],
  providers: [
    UserService,
    AuthService,
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: 'Required',
        email: 'Enter a valid email',
      },
    },
  ],
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss',
})
export class AddUserComponent implements OnInit {
  statusitems = ['Active', 'Inactive'];
  roleitems = ['ADMIN', 'SUPPORT', 'STAFF'];
  status = 'add';
  userId: any;
  userData: any;
  userForm!: FormGroup;
  passwordForm!: FormGroup;
  formErrors: any;
  isFirstLogin: any;
  isLoading = false;
  user: any;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private readonly alerts: TuiAlertService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.initUserForm();
    this.initPasswordForm();
    this.user = this.authService.getLoggedInUser();
    this.route.params.subscribe((params) => {
      this.isFirstLogin = params['status'];
      //newly add
      // this.isPasswordReset = params['isPasswordReset'] === 'true';
      if (this.isFirstLogin) {
        // if (this.isFirstLogin || this.isPasswordReset) {
        this.status = 'edit';
      } else {
        this.userId = this.route.snapshot.paramMap.get('id');
        if (this.userId) {
          this.isLoading = true;
          this.userService.getOneUserById(this.userId).then(
            (res: any) => {
              this.status = 'edit';
              this.userData = res;
              this.isLoading = false;
              this.userForm.get('email')?.disable();

              this.userForm.patchValue(this.userData);
              if (this.userData.isActive === true) {
                this.userForm.get('isActive')?.setValue('Active');
              } else {
                this.userForm.get('isActive')?.setValue('InActive');
              }

              if (this.userData.role === 'SUPER_ADMIN') {
                this.userForm.get('isActive')?.disable();
              } else if (
                this.userData.role === 'ADMIN' &&
                (this.user.role === 'ADMIN' ||
                  this.user.role === 'STAFF' ||
                  this.user.role === 'SUPPORT')
              ) {
                this.userForm.get('isActive')?.disable();
              } else if (
                this.user.role === 'STAFF' ||
                this.user.role === 'SUPPORT'
              ) {
                this.userForm.get('isActive')?.disable();
              }
            },
            (e: any) => {}
          );
        }
      }
    });
  }

  initUserForm(): void {
    this.userForm = this.formBuilder.group({
      email: [
        null,
        Validators.compose([Validators.required, Validators.email]),
      ],
      firstname: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[a-zA-Z]+$/),
        ]),
      ],
      lastname: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[a-zA-Z]+$/),
        ]),
      ],
      role: [null, Validators.compose([Validators.required])],
      mobileNo: [
        null,
        [Validators.required, Validators.pattern(/^[0-9]{0,16}$/)],
      ],
      isActive: ['Active', Validators.compose([Validators.required])],
    });
    this.userForm.valueChanges.subscribe(() => {
      this.formErrors = AppUtils.getFormErrors(this.userForm, this.formErrors);
    });
  }

  initPasswordForm(): void {
    this.passwordForm = this.formBuilder.group(
      {
        password: [
          null,
          Validators.compose([Validators.required, Validators.minLength(4)]),
        ],
        passwordConfirm: [null, Validators.compose([Validators.required])],
      },
      { validator: this.passwordMatchValidator }
    );
    this.passwordForm.valueChanges.subscribe(() => {
      this.formErrors = this.getFormErrors(this.passwordForm, this.formErrors);
    });
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: any } | null {
    const passwordControl = group.get('password');
    const passwordConfirmControl = group.get('passwordConfirm');
    if (!passwordControl || !passwordConfirmControl) {
      return null;
    }
    if (passwordControl.value !== passwordConfirmControl.value) {
      return { passwordMismatch: true };
    } else {
      return null;
    }
  }

  getFormErrors(form: FormGroup, errors: any): any {
    // Implement your logic to get form errors here
    return errors;
  }

  onSubmit() {
    if (this.userForm.valid) {
      this.isLoading = true;

      this.userService
        .createUser({
          ...this.userForm.getRawValue(),
          isActive: this.updateIsActiveControl(),
        })
        .then(() => {
          this.isLoading = false;
          this.alerts
            .open('<p style="color: red">User Added Successfully</p>')
            .subscribe();
          this.navigateToView();
        })
        .catch((e) => {
          this.isLoading = false;
          this.alerts
            .open(`<p style="color: red">${e.message}</p>`, { status: 'error' })
            .subscribe();
        });
    } else {
      // Mark all fields as touched to display validation errors
      this.userForm.markAllAsTouched();
    }
  }

  onUpdate(): void {
    if (this.userForm.valid) {
      this.userForm.get('email')?.enable();
      this.isLoading = true;
      this.userService
        .updateUser({
          _id: this.userId,
          ...this.userForm.getRawValue(),
          isActive: this.updateIsActiveControl(),
        })
        .then(() => {
          if (this.userId === this.authService.getLoggedInUser().userId) {
            this.userService.loggedInUserNameSubject.next(
              `${this.userForm.get('firstname')?.value} ${
                this.userForm.get('lastname')?.value
              }`
            );
          }
          this.isLoading = false;
          this.alerts
            .open('<p style="color: green">User Updated Successfully</p>')
            .subscribe();
          this.navigateToView();
        })
        .catch((e) => {
          this.isLoading = false;
          this.alerts
            .open(`<p style="color: red">${e.message}</p>`, { status: 'error' })
            .subscribe();
        });
    } else {
      // Mark all fields as touched to display validation errors
      this.userForm.markAllAsTouched();
    }
  }

  onChangePassword(): void {
    if (this.passwordForm.valid) {
      const user = this.authService.getLoggedInUser();
      user.isFirstLogin = false;
      user._id = user.userId;
      user.password = this.passwordForm.get('password')?.value;
      delete user.userId;
      this.isLoading = true;
      this.userService
        .updateUser({
          _id: this.userId,
          ...user,
        })
        .then(() => {
          this.isLoading = false;
          this.alerts
            .open(
              '<p style="color: green">User Password Changed Successfully</p>'
            )
            .subscribe();
          this.navigateToView();
        })
        .catch(() => {
          this.isLoading = false;
          this.alerts
            .open(
              '<p style="color: red">User Password Change Unsuccessfull</p>',
              { status: 'error' }
            )
            .subscribe();
        });
    }
  }

  updateIsActiveControl() {
    const isActiveControl = this.userForm.get('isActive');
    if (isActiveControl?.value === 'Active') {
      return true;
      // isActiveControl.setValue(true);
    } else {
      // isActiveControl.setValue(false);
      return false;
    }
  }

  navigateToView(): void {
    // this.router.navigate(['admin/members']);
    this.location.back();
  }

  cancel(): void {
    if (this.isFirstLogin) {
      const user = this.authService.getLoggedInUser();
      user.isFirstLogin = false;
      user._id = user.userId;
      delete user.userId;
      this.isLoading = true;
      this.userService
        .updateUser({
          _id: this.userId,
          ...user,
        })
        .then(() => {
          this.isLoading = false;
          this.navigateToView();
        })
        .catch((error) => {
          this.isLoading = false;
        });
    } else {
      this.navigateToView();
    }
  }
}
